import React, { createContext, useReducer, useContext, useEffect } from 'react';
import api from '../services/api';

// Initial state for the agent context
const initialState = {
  messages: [],
  isTyping: false,
  history: [],
  suggestions: [],
  error: null,
  loading: false,
  sessionId: null
};

// Action types
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  ADD_USER_MESSAGE: 'ADD_USER_MESSAGE',
  ADD_AGENT_MESSAGE: 'ADD_AGENT_MESSAGE',
  SET_TYPING: 'SET_TYPING',
  SET_SUGGESTIONS: 'SET_SUGGESTIONS',
  CLEAR_CHAT: 'CLEAR_CHAT',
  SET_SESSION_ID: 'SET_SESSION_ID',
  LOAD_HISTORY: 'LOAD_HISTORY'
};

// Reducer function
const agentReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
        isTyping: false
      };
    
    case ACTION_TYPES.ADD_USER_MESSAGE:
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: `user-${Date.now()}`,
            text: action.payload,
            isUser: true,
            timestamp: new Date().toISOString()
          }
        ],
        error: null
      };
    
    case ACTION_TYPES.ADD_AGENT_MESSAGE:
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: `agent-${Date.now()}`,
            text: action.payload,
            isUser: false,
            timestamp: new Date().toISOString()
          }
        ],
        isTyping: false
      };
    
    case ACTION_TYPES.SET_TYPING:
      return {
        ...state,
        isTyping: action.payload
      };
    
    case ACTION_TYPES.SET_SUGGESTIONS:
      return {
        ...state,
        suggestions: action.payload
      };
    
    case ACTION_TYPES.CLEAR_CHAT:
      return {
        ...initialState,
        sessionId: state.sessionId
      };
    
    case ACTION_TYPES.SET_SESSION_ID:
      return {
        ...state,
        sessionId: action.payload
      };
    
    case ACTION_TYPES.LOAD_HISTORY:
      return {
        ...state,
        messages: action.payload,
        loading: false
      };
    
    default:
      return state;
  }
};

// Create context
export const AgentContext = createContext();

/**
 * AgentContextProvider component for managing agent state and interactions
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const AgentContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(agentReducer, initialState);
  
  // Initialize chat session
  useEffect(() => {
    const initSession = async () => {
      try {
        // Check for existing session in localStorage
        const savedSessionId = localStorage.getItem('agentSessionId');
        
        if (savedSessionId) {
          // Try to load existing session
          await loadChatHistory(savedSessionId);
        } else {
          // Create a new session if none exists
          await createNewSession();
        }
      } catch (error) {
        console.error('Error initializing chat session:', error);
        dispatch({ 
          type: ACTION_TYPES.SET_ERROR, 
          payload: 'Failed to initialize chat session. Please try again.' 
        });
      }
    };
    
    initSession();
    // We're intentionally not including createNewSession and loadChatHistory in the dependency array
    // to prevent infinite loops, as they are recreated on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Create new chat session
  const createNewSession = async () => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    
    try {
      const response = await api.post('/api/agent/session');
      const sessionId = response.data.sessionId;
      
      // Save session ID to localStorage
      localStorage.setItem('agent_session_id', sessionId);
      
      dispatch({ type: ACTION_TYPES.SET_SESSION_ID, payload: sessionId });
      
      // Get initial suggestions
      await getSuggestions();
      
      dispatch({ type: ACTION_TYPES.SET_LOADING, payload: false });
    } catch (error) {
      console.error('Error creating chat session:', error);
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Failed to create chat session. Please try again.' 
      });
    }
  };
  
  // Load chat history
  const loadChatHistory = async (sessionId) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    
    try {
      const response = await api.get(`/api/agent/history/${sessionId}`);
      
      dispatch({ 
        type: ACTION_TYPES.LOAD_HISTORY, 
        payload: response.data.messages 
      });
      
      // Get suggestions based on chat history
      await getSuggestions();
    } catch (error) {
      console.error('Error loading chat history:', error);
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Failed to load chat history. Please try again.' 
      });
    }
  };
  
  // Send message to agent
  const sendMessage = async (message) => {
    // Add user message to state
    dispatch({ type: ACTION_TYPES.ADD_USER_MESSAGE, payload: message });
    
    // Set agent as typing
    console.log('AgentContext: Setting isTyping to TRUE');
    dispatch({ type: ACTION_TYPES.SET_TYPING, payload: true });
    
    // Clear any previous errors
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: null });
    
    let retryCount = 0;
    const maxRetries = 2;
    
    const attemptSendMessage = async () => {
      try {
        // Ensure we have a valid sessionId
        if (!state.sessionId) {
          console.log('No sessionId available, creating new session before sending message');
          await createNewSession();
          
          // Double-check that session creation worked
          if (!state.sessionId) {
            throw new Error('Failed to create session ID');
          }
        }
        
        // Log the request details for debugging
        console.log('Sending message to agent:', {
          endpoint: '/agent/message',
          payload: { sessionId: state.sessionId, message },
          attempt: retryCount + 1,
          timestamp: new Date().toISOString()
        });
        
        // Try both endpoints to ensure compatibility
        let response;
        try {
          // First try the /agent/message endpoint (frontend convention)
          response = await api.post('/api/agent/message', {
            sessionId: state.sessionId,
            message
          });
        } catch (endpointError) {
          console.warn('Error with /agent/message endpoint, trying /agent/ask fallback:', endpointError);
          
          // If that fails, try the /agent/ask endpoint (backend convention)
          response = await api.post('/api/agent/ask', {
            sessionId: state.sessionId,
            question: message // Use 'question' key for /agent/ask endpoint
          });
        }
        
        // Validate response structure
        if (!response || !response.data) {
          throw new Error('Invalid response from server');
        }
        
        // Log successful response
        console.log('Received response from agent:', {
          data: response.data,
          status: response.status,
          timestamp: new Date().toISOString()
        });
        
        // Extract reply from response, handling different response formats
        const reply = response.data.reply || response.data.message || response.data.response || 
                     (typeof response.data === 'string' ? response.data : 'Sorry, I could not process your request at this time.');
        
        // Extract answer string from response object if needed
        const responseText = typeof reply === 'object' && reply.answer 
          ? reply.answer  // Extract answer from object
          : reply;        // Use as is if already a string
        
        console.log('Processing agent response:', { 
          originalReply: reply, 
          extractedText: responseText,
          type: typeof reply
        });
        
        // Add agent response to state
        dispatch({ 
          type: ACTION_TYPES.ADD_AGENT_MESSAGE, 
          payload: responseText
        });
        
        // Get new suggestions based on conversation
        await getSuggestions();
        
        return true; // Success
      } catch (error) {
        console.error(`Error sending message (attempt ${retryCount + 1}/${maxRetries + 1}):`, error);
        
        // Add more detailed error logging
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          console.error('Error response data:', error.response.data);
          console.error('Error response status:', error.response.status);
          console.error('Error response headers:', error.response.headers);
          
          // Special handling for specific status codes
          if (error.response.status === 404) {
            console.warn('Endpoint not found. This may indicate a mismatch between frontend and backend API routes.');
          }
        } else if (error.request) {
          // The request was made but no response was received
          console.error('No response received from server. Request details:', {
            url: error.config?.url,
            method: error.config?.method,
            data: error.config?.data,
            headers: error.config?.headers
          });
          console.warn('This may indicate that the backend server is not running or not accessible.');
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error setting up request:', error.message);
        }
        
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Retrying message send (attempt ${retryCount + 1}/${maxRetries + 1})...`);
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          return attemptSendMessage();
        }
        
        // All retries failed
        // Add fallback agent message for better UX
        dispatch({ 
          type: ACTION_TYPES.ADD_AGENT_MESSAGE, 
          payload: 'I apologize, but I encountered a technical issue. Please try again or contact support if the problem persists.'
        });
        
        // Set error state
        dispatch({ 
          type: ACTION_TYPES.SET_ERROR, 
          payload: `Failed to send message after ${maxRetries + 1} attempts. Please check your connection and try again.` 
        });
        
        return false; // Failed after all retries
      }
    };
    
    try {
      await attemptSendMessage();
      // Only turn off typing indicator after successful response
      // The ADD_AGENT_MESSAGE action in the reducer will handle this
    } catch (error) {
      // Only turn off typing indicator if there's an error
      dispatch({ type: ACTION_TYPES.SET_TYPING, payload: false });
    }
  };
  
  // Get suggestions based on conversation context
  const getSuggestions = async () => {
    try {
      // Only make the API call if we have a sessionId
      if (state.sessionId) {
        const response = await api.get(`/api/agent/suggestions`, {
          params: { sessionId: state.sessionId }
        });
        
        dispatch({ 
          type: ACTION_TYPES.SET_SUGGESTIONS, 
          payload: response.data.suggestions 
        });
      } else {
        console.log('Skipping suggestions request - no sessionId available yet');
      }
    } catch (error) {
      console.error('Error getting suggestions:', error);
      // Don't set error state for suggestions failure
    }
  };
  
  // Clear chat history
  const clearChat = async () => {
    try {
      // Try to delete the chat history on the server
      if (state.sessionId) {
        try {
          await api.delete(`/api/agent/history/${state.sessionId}`);
        } catch (deleteError) {
          // Log the error but continue with local clearing
          console.error('Error deleting chat history from server:', deleteError);
        }
      }
      
      // Always clear the chat locally regardless of server response
      dispatch({ type: ACTION_TYPES.CLEAR_CHAT });
      
      // Create new session
      await createNewSession();
    } catch (error) {
      console.error('Error clearing chat:', error);
      
      // Even if creating a new session fails, still clear the local chat
      // This ensures the UI resets to welcome state
      dispatch({ type: ACTION_TYPES.CLEAR_CHAT });
    }
  };
  
  // Context value
  const contextValue = {
    state,
    sendMessage,
    clearChat,
    createNewSession
  };
  
  return (
    <AgentContext.Provider value={contextValue}>
      {children}
    </AgentContext.Provider>
  );
};

// Custom hook for using the agent context
export const useAgent = () => {
  const context = useContext(AgentContext);
  
  if (!context) {
    throw new Error('useAgent must be used within an AgentContextProvider');
  }
  
  return context;
};
